<template>
  <div v-if="showSpinner" class="shamrock-wrapper">
    <ShamrockSpinner />
  </div>
  <div v-else-if="plays.length">
    <component :is="lotteryPage" />
  </div>
</template>

<script lang="ts" setup>
import {
  KnownProductType,
  LotteryId,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import {
  TheNebraskaMyDayLotteryPage,
  TheNumberLotteryPage,
  ThePickLotteryPage,
  ThePowerballXsAndOsPage,
} from '#components'
import { untilNotEmptyClient } from '~/utils/until'

const { logTrackingError } = useTrackingErrorLog()
const { pushAddToCartEvent, pushRemoveFromCartEvent, pushViewItemEvent } = useTracking()

const { createECommerceEventData } = useTrackingData()

const {
  addOns,
  applyAddOnsFromQuery,
  applyTicketTypeFromQuery,
  completePlays,
  config,
  currentPrice,
  lotteryId,
  plays,
  ticketType,
} = usePlaySlip()

const { isNumberLottery } = useLotteryType(lotteryId)

const hasRestoredPlays = ref(false)

const showSpinner = ref(false)
let spinnerTimeout: null | ReturnType<typeof setTimeout> = null

const CUSTOM_LOTTERY_PAGES: Partial<Record<LotteryId, Component>> = {
  [LotteryId.NebraskaMyDay]: TheNebraskaMyDayLotteryPage,
  [LotteryId.PowerBallXsAndOs]: ThePowerballXsAndOsPage,
}

const lotteryPage = computed(() => {
  if (lotteryId in CUSTOM_LOTTERY_PAGES) {
    return CUSTOM_LOTTERY_PAGES[lotteryId]
  }

  return isNumberLottery.value ? TheNumberLotteryPage : ThePickLotteryPage
})

const generateEventData = async () =>
  await createECommerceEventData({
    currentPrice: currentPrice.value,
    lotteryId,
    plays: plays.value,
    ticketType: ticketType.value,
    type: KnownProductType.Lottery,
  })

const trackViewItemEvent = async () => {
  try {
    pushViewItemEvent(await generateEventData())
  } catch (err) {
    logTrackingError('view_item', err)
  }
}

const trackAddToCartEvent = async () => {
  try {
    pushAddToCartEvent(await generateEventData())
  } catch (err) {
    logTrackingError('add_to_cart', err)
  }
}

const trackRemoveFromCartEvent = async () => {
  try {
    pushRemoveFromCartEvent(await generateEventData())
  } catch (err) {
    logTrackingError('remove_from_cart', err)
  }
}

untilNotEmptyClient(plays, trackViewItemEvent)

watch(
  () => addOns.value.length,
  () => {
    applyAddOnsFromQuery()
  },
  { immediate: true },
)

watch(
  () => completePlays.value,
  (newValue, oldValue) => {
    if (!oldValue.length && !hasRestoredPlays.value) {
      hasRestoredPlays.value = true
      return
    } // do not trigger on init but after first play is added
    const diff = newValue.length - oldValue.length
    if (diff > 0) {
      trackAddToCartEvent()
    }
    if (diff < 0) {
      trackRemoveFromCartEvent()
    }
  },
)

watch(
  () => plays.value.length,
  (newLength) => {
    if (newLength > 0) {
      showSpinner.value = false
      if (spinnerTimeout) {
        clearTimeout(spinnerTimeout)
        spinnerTimeout = null
      }
    }
  },
)

watch(config, applyTicketTypeFromQuery)

onMounted(() => {
  spinnerTimeout = setTimeout(() => {
    if (!plays.value.length) {
      showSpinner.value = true
    }
  }, 500)
})

onBeforeUnmount(() => {
  if (spinnerTimeout) {
    clearTimeout(spinnerTimeout)
    spinnerTimeout = null
  }
})
</script>

<style lang="scss" scoped>
.header {
  /* stylelint-disable-next-line selector-class-pattern */
  :deep(.lc-header__wrap) {
    height: 4rem;
    padding-bottom: 0;
  }
}

.shamrock-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  width: 100%;
  height: 100%;
  background: #eef9ff;
}
</style>
